import React, { useEffect, useState, useCallback } from 'react';
import { useNews } from '../../hooks/useNews';
import { NewsPost } from '../../types/EducationHub';
import s from '../../styles/AdminNews.module.css';

// ── Helpers ───────────────────────────────────────────────────────────────────

const EMPTY_FORM = {
  title: '', content: '', excerpt: '', category: '',
  slug: '', coverImageUrl: '', description: '', isPublished: false,
};

// ── Main component ────────────────────────────────────────────────────────────

const AdminNews: React.FC = () => {
  const { fetchAdminNews, createNewsPost, updateNewsPost, deleteNewsPost } = useNews();

  const [news, setNews] = useState<NewsPost[]>([]);
  const [loadingNews, setLoadingNews] = useState(false);

  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    setLoadingNews(true);
    try {
      const list = await fetchAdminNews();
      setNews(list);
    } finally {
      setLoadingNews(false);
    }
  }, [fetchAdminNews]);

  useEffect(() => { reload(); }, [reload]);

  // Categories already in use, for the datalist
  const categoryOptions = Array.from(new Set(news.map(n => n.category).filter(Boolean)));

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleEdit = (post: NewsPost) => {
    setEditingId(post._id);
    setForm({
      title: post.title ?? '',
      content: post.content ?? '',
      excerpt: post.excerpt ?? '',
      category: post.category ?? '',
      slug: post.slug ?? '',
      coverImageUrl: post.coverImageUrl ?? '',
      description: post.description ?? '',
      isPublished: post.isPublished,
    });
    setFormError('');
  };

  const handleCancel = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormError('');
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.content.trim() || !form.excerpt.trim() || !form.category.trim()) {
      setFormError('Title, content, excerpt, and category are required.');
      return;
    }
    setSaving(true);
    setFormError('');
    try {
      const payload = {
        title: form.title.trim(),
        content: form.content,
        excerpt: form.excerpt.trim(),
        category: form.category.trim(),
        slug: form.slug.trim() || undefined,
        coverImageUrl: form.coverImageUrl.trim() || undefined,
        description: form.description.trim() || undefined,
        isPublished: form.isPublished,
      };
      if (editingId !== null) {
        await updateNewsPost(editingId, payload);
      } else {
        await createNewsPost(payload);
      }
      handleCancel();
      await reload();
    } catch {
      setFormError('Failed to save news post. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this news post?')) return;
    await deleteNewsPost(id);
    await reload();
  };

  const handleTogglePublished = async (post: NewsPost) => {
    await updateNewsPost(post._id, { isPublished: !post.isPublished });
    await reload();
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className={s.page}>
      <h2 className={s.pageTitle}>📰 News Management</h2>

      {/* ── Form ───────────────────────────────────────────────────────────── */}
      <section className={s.panel}>
        <h3 className={s.panelTitle}>{editingId !== null ? 'Edit News Post' : 'Add News Post'}</h3>
        <div className={s.formGrid}>
          <label className={s.formLabel}>
            Title *
            <input className={s.input} placeholder="News post title" value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
          </label>
          <label className={s.formLabel}>
            Category *
            <input className={s.input} placeholder="e.g. Product News" value={form.category}
              list="news-categories"
              onChange={e => setForm(f => ({ ...f, category: e.target.value }))} />
            <datalist id="news-categories">
              {categoryOptions.map(c => <option key={c} value={c} />)}
            </datalist>
          </label>
          <label className={s.formLabel}>
            Slug
            <input className={s.input} placeholder="e.g. digital-signatures-launch" value={form.slug}
              onChange={e => setForm(f => ({ ...f, slug: e.target.value }))} />
          </label>
          <label className={s.formLabel}>
            Cover Image URL
            <input className={s.input} placeholder="https://..." value={form.coverImageUrl}
              onChange={e => setForm(f => ({ ...f, coverImageUrl: e.target.value }))} />
          </label>
          <label className={`${s.formLabel} ${s.fullWidth}`}>
            Excerpt (short summary shown on cards) *
            <input className={s.input} value={form.excerpt}
              onChange={e => setForm(f => ({ ...f, excerpt: e.target.value }))} />
          </label>
          <label className={`${s.formLabel} ${s.fullWidth}`}>
            Description
            <textarea className={s.textarea} rows={2} value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
          </label>
          <label className={`${s.formLabel} ${s.fullWidth}`}>
            Content *
            <textarea className={s.textarea} rows={6} value={form.content}
              onChange={e => setForm(f => ({ ...f, content: e.target.value }))} />
          </label>
          <label className={s.checkboxLabel}>
            <input type="checkbox" checked={form.isPublished}
              onChange={e => setForm(f => ({ ...f, isPublished: e.target.checked }))} />
            Published (visible to users)
          </label>
        </div>
        {formError && <p className={s.error}>{formError}</p>}
        <div className={s.formActions}>
          <button className={s.btnSave} onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : editingId !== null ? 'Update' : 'Add News Post'}
          </button>
          {editingId !== null && (
            <button className={s.btnCancel} onClick={handleCancel}>Cancel</button>
          )}
        </div>
      </section>

      {/* ── Table ──────────────────────────────────────────────────────────── */}
      <section className={s.panel}>
        <h3 className={s.panelTitle}>All News Posts ({news.length})</h3>
        {loadingNews ? <p className={s.loading}>Loading…</p> : (
          <div className={s.tableWrapper}>
            <table className={s.table}>
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Category</th>
                  <th>Slug</th>
                  <th>Published</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {news.map(post => (
                  <tr key={post._id}>
                    <td>{post.title}</td>
                    <td>{post.category ?? '—'}</td>
                    <td>{post.slug ?? '—'}</td>
                    <td>
                      <button
                        className={post.isPublished ? s.badgeActive : s.badgeInactive}
                        onClick={() => handleTogglePublished(post)}
                        title="Toggle published"
                      >
                        {post.isPublished ? 'Published' : 'Draft'}
                      </button>
                    </td>
                    <td className={s.actions}>
                      <button className={s.btnEdit} onClick={() => handleEdit(post)}>Edit</button>
                      <button className={s.btnDelete} onClick={() => handleDelete(post._id)}>Delete</button>
                    </td>
                  </tr>
                ))}
                {news.length === 0 && (
                  <tr><td colSpan={5} className={s.empty}>No news posts yet. Add one above.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default AdminNews;
